import { useEffect, useState } from 'react'
import useUser from 'shared/hooks/use-user'
import { useFilter } from 'shared/route-query/hooks/use-filter/index'

export type FilterWithDateType = { dateSince: string | null; dateTill: string | null }

export const useFilterWithDate = <T extends FilterWithDateType>(
  defaultStaticFilter: T,
  getDefaultDateRange?: (timeZone?: string) => [string, string],
  isStatic?: boolean,
) => {
  const { user } = useUser()
  const [defaultFilter, setDefaultFilter] = useState<T | undefined>(
    getDefaultDateRange ? undefined : defaultStaticFilter,
  )

  const { filter, setFilter } = useFilter(defaultStaticFilter, {
    isStatic,
    getDefaultDateRange,
    defaultDynamicFilter: defaultFilter,
  })

  useEffect(() => {
    if (!user || !getDefaultDateRange) return

    const [defaultDateSince, defaultDateTill] = getDefaultDateRange(user?.timezone)

    setDefaultFilter(prev => ({
      ...(prev ?? defaultStaticFilter),
      dateSince: defaultDateSince,
      dateTill: defaultDateTill,
    }))
  }, [defaultStaticFilter, getDefaultDateRange, user])

  useEffect(() => {
    if (filter.dateTill || filter.dateSince || !defaultFilter?.dateSince || !defaultFilter.dateTill)
      return
    if (user) {
      setFilter(prev => ({
        ...prev,
        dateSince: defaultFilter.dateSince,
        dateTill: defaultFilter.dateTill,
      }))
    }
  }, [
    user,
    defaultFilter?.dateTill,
    filter.dateSince,
    filter.dateTill,
    setFilter,
    defaultFilter?.dateSince,
  ])

  return { filter, setFilter, defaultFilter: defaultFilter ?? defaultStaticFilter }
}
